import { useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { ChannelConfiguration, Role, Visibility } from '@connect-x-app/shared-domain-models';
import { Option } from '@connect-x-app/shared-types';
import { FormValidator, ValidatorResponse } from '@connect-x-app/shared-utils';
import { FormValidationErrors, useForm } from '@connect-x-app/web-state';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Banner, InputField, SearchableSelect } from '@connect-x-app/web-ui';

import { ModalForm } from '../../../../roles/components';

type Props = {
  canViewRoles: boolean;
  channel?: ChannelConfiguration;
  channels: ChannelConfiguration[];
  closeModal: () => void;
  isLoading: boolean;
  onSubmit: (value: ChannelConfiguration) => void;
  roles: Role[];
};

const initialForm: ChannelConfiguration = {
  description: '',
  fixedRoles: [],
  id: '',
  title: '',
  visibility: Visibility.PUBLIC,
};

const validateFixedRoles = (
  validator: FormValidator,
  fixedRoles: string[],
  fixedRolesTranslation: string,
): ValidatorResponse[] => {
  const hasLengthValidation = validator.dropdownRequired(fixedRoles.length, fixedRolesTranslation);

  if (!hasLengthValidation.isValid) {
    return [hasLengthValidation];
  }

  return [];
};

const validateTitle = (
  validator: FormValidator,
  title: string,
  existingChannelConfigurations: ChannelConfiguration[],
  t: TFunction,
  channelConfiguration?: ChannelConfiguration,
): ValidatorResponse | undefined => {
  const requiredValidation = validator.required(title);
  const maxLengthValidation = validator.maxLength(title, 100);

  if (!requiredValidation.isValid) {
    return requiredValidation;
  }

  if (!maxLengthValidation.isValid) {
    return maxLengthValidation;
  }

  const filteredExistingChannels = existingChannelConfigurations.filter(e => e.title !== channelConfiguration?.title);

  if (filteredExistingChannels.some(r => r.title.toUpperCase().trim() === title.toUpperCase().trim())) {
    return {
      isValid: false,
      message: t('CHANNEL_CONFIG.FORM.ERROR.TITLE_DUPLICATE'),
    };
  }

  return undefined;
};

const validateDescription = (validator: FormValidator, description: string): ValidatorResponse | undefined => {
  const maxLengthValidation = validator.maxLength(description, 500);
  if (!maxLengthValidation.isValid) {
    return maxLengthValidation;
  }

  return undefined;
};

const validateForm = (
  formValue: ChannelConfiguration,
  existingChannelConfigurations: ChannelConfiguration[],
  t: TFunction,
  channelConfiguration?: ChannelConfiguration,
): FormValidationErrors<ChannelConfiguration> => {
  const validator = new FormValidator();

  return {
    description: validateDescription(validator, formValue?.description ?? ''),
    fixedRoles: validateFixedRoles(validator, formValue.fixedRoles, t('CHANNEL_CONFIG.FORM.PARTICIPANT')),
    title: validateTitle(validator, formValue.title, existingChannelConfigurations, t, channelConfiguration),
  };
};

const mapRolesToOptions = (roles: Role[]): Option[] => roles.map(role => ({ label: role.airlineRole, value: role.externalId }));

export const EditChannelConfigurationModal = ({
  canViewRoles,
  channel,
  channels,
  closeModal,
  isLoading,
  onSubmit,
  roles,
}: Props) => {
  const { t } = useTranslation();

  const form = useForm<ChannelConfiguration>({
    initialForm: channel ?? initialForm,
    submitForm: (channel: ChannelConfiguration) => onSubmit(channel),
    validateForm: values => validateForm(values, channels, t, channel),
  });

  const fixedRolesOptions = useMemo(() => {
    return mapRolesToOptions(roles);
  }, [roles]);

  const onChangeFixedRoles = (newValue: SingleValue<Option> | MultiValue<Option>, name: string) => {
    form.setAttribute(
      (newValue as MultiValue<Option>).map(option => option.value),
      name,
    );
  };

  const selectedFixedRoles = useMemo(() => {
    return fixedRolesOptions.filter(fo => form.values.fixedRoles.indexOf(fo.value) > -1);
  }, [fixedRolesOptions, form.values.fixedRoles]);

  return (
    <ModalForm
      disabled={!form.isValid || !form.isDirty}
      headerTitle={channel ? t('CHANNEL_CONFIG.FORM.UPDATE') : t('CHANNEL_CONFIG.FORM.ADD')}
      isDirty={form.isDirty}
      isSubmitting={isLoading}
      onCancel={closeModal}
      onSubmit={form.submit}
      submitButtonText={channel ? t('SHARED.BUTTONS.SAVE') : t('SHARED.BUTTONS.ADD')}
      testId={ADMIN_TEST_IDS.channelConfig.modal}
    >
      <Banner message={t('CHANNEL_CONFIG.FORM.BANNER')} variant="accent" />
      <InputField
        label={t('CHANNEL_CONFIG.FORM.NAME')}
        name="title"
        onChange={form.setAttribute}
        required
        showInlineBottomError
        validation={form.validationErrors?.title}
        value={form.values?.title}
      />
      <InputField
        label={t('CHANNEL_CONFIG.FORM.DESCRIPTION')}
        name="description"
        onChange={form.setAttribute}
        showInlineBottomError
        tooltip={t('CHANNEL_CONFIG.FORM.TOOLTIP.DESCRIPTION')}
        validation={form.validationErrors?.description}
        value={form.values?.description}
      />
      <SearchableSelect
        disabled={!canViewRoles}
        filterOption={(option: Option, inputValue: string): boolean =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        }
        isModal
        label={t('CHANNEL_CONFIG.FORM.FIXED_ROLES')}
        name="fixedRoles"
        onChange={onChangeFixedRoles}
        options={fixedRolesOptions}
        required
        searchableSelectConfig={{
          customNoOptionsAvailable: t('CHANNEL_CONFIG.FORM.NO_OPTIONS'),
          placeholder: t('CHANNEL_CONFIG.FORM.PLACEHOLDER.FIXED_ROLES'),
        }}
        selectedOptions={selectedFixedRoles}
        showInlineBottomError
        tooltip={!canViewRoles ? t('CHANNEL_CONFIG.TABLE.PARTICIPANTS_TOOLTIP_MODAL') : undefined}
        validation={form.validationErrors?.fixedRoles?.[0]}
      />
    </ModalForm>
  );
};
